import { ampli } from 'ampli';
import CreateNewProject from 'assets/CreateNewProject.png';
import { HeadLine } from 'components/HeadLine/HeadLine';
import InvitationCard from 'components/InvitationCard/InvitationCard';
import LinkButton from 'components/LinkButton/LinkButton';
import Loading from 'components/Loading/Loading';
import PrimaryButton from 'components/PrimaryButton/PrimaryButton';
import ProjectCard from 'components/ProjectCard/ProjectCard';
import Gleap from 'gleap';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { checkIfIsOrgaUser } from 'services/CheckIfUserIsAdmin';
import { sendToGTag } from 'services/GTagHelper';
import { MODALTYPE, ModalStore } from 'stores/private/ModalStore';
import { OrganisationStore } from 'stores/private/OrganisationStore';
import { ProjectStore } from 'stores/private/ProjectStore';
import { UsersStore } from 'stores/private/UsersStore';
import Swal from 'sweetalert2';
import './Dashboard.scss';

interface DashboardProps {
  usersStore?: UsersStore;
  projectStore?: ProjectStore;
  modalStore?: ModalStore;
  organisationStore?: OrganisationStore;
}

function Dashboard({
  usersStore,
  projectStore,
  modalStore,
  organisationStore,
}: DashboardProps) {
  const navigate = useNavigate();
  const projects = projectStore?.projects;
  const [onboardingShown, setOnboardingShown] = useState(false);
  const [projectOrgas, setProjectOrgas] = useState({} as any);
  const [reloadingPage, setReloadingPage] = useState(false);

  const isEvening = new Date().getHours() > 17;
  const welcomeSalut = isEvening ? 'Good Evening' : 'Hey';
  const welcomeSalutIcon = isEvening ? '🌜' : '👋';

  useEffect(() => {
    if (usersStore?.currentUser && usersStore?.currentUser?.id) {
      if (usersStore) {
        usersStore.findInvitations();
      }
    }
  }, [usersStore?.currentUser]);

  useEffect(() => {
    projectStore!.clearCurrentProject();
    ampli.homeScreenViewed();

    if (projectStore) {
      projectStore.getProjectsUnreadCount();
    }
  }, []);

  useEffect(() => {
    if (projects) {
      const projectOrgasInternal = {};
      for (let i = 0; i < projects?.length; i++) {
        const project = projects[i];
        const projectOrgaKey = project.organisation?.id ?? 'UNDEFINED';
        if (!projectOrgasInternal[projectOrgaKey]) {
          projectOrgasInternal[projectOrgaKey] = {
            organisation: project.organisation,
            projects: [],
          };
        }

        projectOrgasInternal[projectOrgaKey].projects.push(project);
      }

      if (organisationStore?.organisations) {
        for (let i = 0; i < organisationStore?.organisations.length; i++) {
          const organisation = organisationStore?.organisations[i];
          const projectOrgaKey = organisation.id ?? 'UNDEFINED';
          if (!projectOrgasInternal[projectOrgaKey]) {
            projectOrgasInternal[projectOrgaKey] = {
              organisation,
              projects: [],
            };
          }
        }
      }

      setProjectOrgas(projectOrgasInternal);
    }
  }, [projects, organisationStore?.organisations]);

  useEffect(() => {
    if (
      !onboardingShown &&
      usersStore?.currentUser &&
      (!usersStore?.currentUser?.completedOnboarding ||
        !usersStore?.currentUser?.confirmed)
    ) {
      localStorage.setItem('upsell-modal-shown', new Date().toISOString());

      setOnboardingShown(true);
      modalStore!.openModal(MODALTYPE.ONBOARDING);

      sendToGTag('HpkfCKq8mNIDELiL8IEC');
      ampli.onboardingStarted();
      Gleap.trackEvent('Onboarding started');
    }

    if (usersStore?.currentUser?.completedOnboarding) {
      sendToGTag('0du4CNrIx9IDELiL8IEC');
    }
  }, [usersStore?.currentUser, onboardingShown]);

  /*const renderMyIssues = () => {
    if (organisationStore?.loadingMyIssues) {
      return <Loading />;
    }

    return (
      <div className="main-tasks">
        <div className="my-tasks-header">
          <div className="dashboard-headline">Your open tasks</div>
        </div>
        <div className="my-tasks-list">
          {organisationStore?.myIssues &&
            organisationStore?.myIssues.length === 0 && (
              <div className="no-tasks-container">
                <HeadLine
                  title="✅ All tasks are done"
                  subtitle="No open tasks are assigned to you."
                />
              </div>
            )}
          {organisationStore?.myIssues &&
            organisationStore?.myIssues.map((myIssue: Bug) => {
              return <IssueCard key={myIssue.id} bug={myIssue} />;
            })}
        </div>
      </div>
    );
  };*/

  const renderProjects = () => {
    if (usersStore!.invitations.length === 0 && projects?.length === 0) {
      return (
        <>
          <div className="dashboard-headline">Projects</div>
          <div className="no-projects-container">
            <img
              src={CreateNewProject}
              alt="No projects created yet"
              className="no-projects-image"
            />
            <HeadLine
              title="No projects yet"
              subtitle="Create a project to get started."
            />
            <PrimaryButton
              icon="plus"
              className="hide-on-mobile"
              label="Create project"
              onClick={() => {
                navigate(`/projects/create/`);
              }}
            />
          </div>
        </>
      );
    }

    return Object.keys(projectOrgas).map((projectOrgaKey) => {
      const projectOrga = projectOrgas[projectOrgaKey];
      const projects = projectOrga.projects ?? [];

      const isAdmin = checkIfIsOrgaUser(
        usersStore?.currentUser,
        projectOrga.organisation,
      );

      const renderTrialBanner = () => {
        if (
          projectOrga.organisation.subscription?.planID === 'free-trial' &&
          projectOrga.organisation?.freeTrialUntil
        ) {
          return (
            <div className="trial-tag">
              <div className="trial-tag-content">
                Free trial until{' '}
                {moment(projectOrga.organisation?.freeTrialUntil).format(
                  'MMM Do',
                )}{' '}
                ✨
              </div>
              <LinkButton
                label="Upgrade now"
                onClick={() => {
                  navigate(
                    `/organization/${projectOrga.organisation.id}/billing`,
                  );
                }}
              />
            </div>
          );
        }

        if (
          projectOrga.organisation.subscription?.subscriptionStatus ===
          'trialing'
        ) {
          return (
            <div className="trial-tag">
              <div className="trial-tag-content">Free trial ✨</div>
              <LinkButton
                label="Manage"
                onClick={() => {
                  navigate(
                    `/organization/${projectOrga.organisation.id}/billing`,
                  );
                }}
              />
            </div>
          );
        }

        if (projectOrga.organisation.subscription?.planID === 'free') {
          return (
            <div className="trial-tag">
              <div className="trial-tag-content">Your trial expired</div>
              <LinkButton
                label="Upgrade now 🚀"
                onClick={() => {
                  navigate(
                    `/organization/${projectOrga.organisation.id}/billing`,
                  );
                }}
              />
            </div>
          );
        }

        return null;
      };

      return (
        <div className="organisation-group" key={projectOrga.organisation.id}>
          <div className="organisation-header">
            <div className="organisation-header-headline">
              {projectOrga.organisation.name}
            </div>
            <div className="organisation-header-free-trial">
              {renderTrialBanner()}
            </div>
            <div className="organisation-header-actions">
              {isAdmin && (
                <>
                  <LinkButton
                    label="Team"
                    iconSideRight={false}
                    icon="user-plus"
                    onClick={() => {
                      navigate(
                        `/organization/${projectOrga.organisation.id}/team`,
                      );
                    }}
                  />
                  <LinkButton
                    label="Settings"
                    iconSideRight={false}
                    icon="gear"
                    onClick={() => {
                      navigate(`/organization/${projectOrga.organisation.id}/`);
                    }}
                  />
                </>
              )}
            </div>
          </div>
          <div className="project-cards">
            {projects.map((project) => {
              return (
                <ProjectCard
                  organization={projectOrga.organisation}
                  key={project.id}
                  project={project}
                />
              );
            })}
            <LinkButton
              iconSideRight={false}
              label="Create project"
              icon="plus"
              className="create-project-button hide-on-mobile"
              onClick={() => {
                if (isAdmin) {
                  navigate(
                    `/projects/create?orga=${projectOrga.organisation.id}`,
                  );
                } else {
                  Swal.fire({
                    text: 'You must have organization admin permissions to create projects.',
                    showCancelButton: false,
                    confirmButtonText: `Ok`,
                  });
                }
              }}
            />
          </div>
        </div>
      );
    });
  };

  const renderContentCards = () => {
    if (projectStore!.loadingProjects) {
      return (
        <div className="project-loading">
          <Loading />
        </div>
      );
    }

    return (
      <>
        <div className="dashboard-content">
          <div className="organisations-container">
            <div className="dashboard--header">
              <HeadLine
                title={`${welcomeSalut} ${usersStore?.currentUser?.firstName} ${welcomeSalutIcon}`}
              />
            </div>
            {usersStore!.invitations.length > 0 && (
              <>
                <div className="dashboard-headline">Pending invitations</div>
                <div className="invitations-list project-cards mb-30">
                  {usersStore!.invitations.map((invitation, key) => {
                    return <InvitationCard invitation={invitation} key={key} />;
                  })}
                </div>
              </>
            )}
            {renderProjects()}
          </div>
          {/** <div className="my-issues-container">{renderMyIssues()}</div> */}
        </div>
      </>
    );
  };

  if (projectStore!.loadingFailed) {
    return (
      <div className="loading-failed">
        <div className='title mb-10'>Connection lost ❤️‍🩹</div>
        <div>
          It looks like you're not connected to the internet.<br />Give it another try or reach out to us for assistance.</div>
        <PrimaryButton
          isLoading={reloadingPage}
          label="Reload app"
          onClick={() => {
            setReloadingPage(true);
            window.location.reload();
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="onboarding-welcome-container">{renderContentCards()}</div>
    </>
  );
}

export default inject(
  'usersStore',
  'projectStore',
  'organisationStore',
  'modalStore',
)(observer(Dashboard));
